import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Tag } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", type: "expense", color: "#6366F1", icon: "tag" };
const PRESET_COLORS = ["#6366F1", "#10B981", "#EF4444", "#F59E0B", "#06B6D4", "#8B5CF6", "#EC4899", "#64748B"];

export default function Categories() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => { const { data } = await api.get("/categories"); setItems(data); };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name) { toast.error("İsim zorunlu"); return; }
    try {
      if (editing) await api.put(`/categories/${editing.id}`, form);
      else await api.post("/categories", form);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Silmek istiyor musunuz?")) return;
    await api.delete(`/categories/${id}`); toast.success("Silindi"); await load();
  };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, type: c.type, color: c.color, icon: c.icon || "tag" }); setOpen(true); };

  const income = items.filter((c) => c.type === "income");
  const expense = items.filter((c) => c.type === "expense");

  return (
    <div className="space-y-6 slide-up" data-testid="categories-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-indigo-400 mb-2">Yapılandırma</div>
          <h1 className="text-4xl font-bold" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Kategoriler ve Etiketler</h1>
          <p className="text-[#94A3B8] mt-1 text-sm">İşlemlerinizi kategorilere ayırarak daha iyi analiz edin.</p>
        </div>
        <Button data-testid="new-category-btn" onClick={openNew} className="bg-indigo-500 hover:bg-indigo-600"><Plus className="w-4 h-4 mr-2"/>Yeni Kategori</Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {[{ title: "Gelir Kategorileri", list: income, tone: "emerald" }, { title: "Gider Kategorileri", list: expense, tone: "red" }].map((group) => (
          <div key={group.title} className="card-elev p-6">
            <div className="font-semibold text-lg mb-4">{group.title}</div>
            {group.list.length === 0 ? (
              <div className="text-sm text-[#94A3B8] py-8 text-center">
                <Tag className="w-8 h-8 mx-auto mb-2 text-[#1E293B]"/>Henüz kategori yok.
              </div>
            ) : (
              <div className="space-y-2">
                {group.list.map((c) => (
                  <div key={c.id} className="flex items-center justify-between py-2 border-b border-[#1E293B] last:border-0" data-testid={`category-row-${c.id}`}>
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${c.color}20`, border: `1px solid ${c.color}50` }}>
                        <Tag className="w-4 h-4" style={{ color: c.color }} />
                      </div>
                      <span className="font-medium">{c.name}</span>
                    </div>
                    <div className="flex gap-1">
                      <button data-testid={`edit-cat-${c.id}`} onClick={() => openEdit(c)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-indigo-400"><Pencil className="w-3.5 h-3.5"/></button>
                      <button data-testid={`delete-cat-${c.id}`} onClick={() => remove(c.id)} className="p-1.5 rounded-lg text-[#94A3B8] hover:text-red-400"><Trash2 className="w-3.5 h-3.5"/></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131D31] border-[#1E293B] text-white">
          <DialogHeader><DialogTitle>{editing ? "Kategoriyi Düzenle" : "Yeni Kategori"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs text-[#94A3B8]">İsim</Label>
              <Input data-testid="cat-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-[#0F172A] border-[#1E293B] mt-1" />
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Tür</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="cat-type" className="bg-[#0F172A] border-[#1E293B] mt-1"><SelectValue/></SelectTrigger>
                <SelectContent className="bg-[#131D31] border-[#1E293B]">
                  <SelectItem value="income">Gelir</SelectItem>
                  <SelectItem value="expense">Gider</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#94A3B8]">Renk</Label>
              <div className="flex gap-2 mt-2 flex-wrap">
                {PRESET_COLORS.map((c) => (
                  <button key={c} type="button" data-testid={`color-${c}`} onClick={() => setForm({ ...form, color: c })}
                    className={`w-8 h-8 rounded-lg border-2 ${form.color === c ? "border-white" : "border-transparent"}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>İptal</Button>
            <Button onClick={save} data-testid="cat-save-btn" className="bg-indigo-500 hover:bg-indigo-600">{editing ? "Güncelle" : "Kaydet"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
